import { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { uuid } from '../../../common/uuid';
import type { ViewModel } from '../Models/ViewModel';

type CreateChildViewDialogProps = {
	parent: ViewModel;
	views: ViewModel[];
	onCreate: (view: ViewModel) => void;
	onClose: () => void;
};

function routePattern(path: string): string {
	return path
		.split('/')
		.filter(Boolean)
		.map((segment) => (segment.startsWith(':') ? ':' : segment.toLowerCase()))
		.join('/');
}

export function CreateChildViewDialog({ parent, views, onCreate, onClose }: CreateChildViewDialogProps) {
	const parentPath = parent.RoutePath!.trim().replace(/\/+$/, '');
	const [title, setTitle] = useState(`${parent.Title || 'View'} Details`);
	const [routePath, setRoutePath] = useState(() => {
		const parameterNames = new Set(
			parentPath
				.split('/')
				.filter((segment) => segment.startsWith(':'))
				.map((segment) => segment.slice(1).replace(/\?$/, '').toLowerCase()),
		);
		let name = 'id';
		let suffix = 1;
		while (parameterNames.has(name.toLowerCase())) {
			name = suffix === 1 ? 'childId' : `childId${suffix}`;
			suffix += 1;
		}
		return `${parentPath}/:${name}`;
	});
	const path = routePath.trim();
	const duplicate = views.some((view) => view.RoutePath && routePattern(view.RoutePath) === routePattern(path));
	const invalidParent = !path.startsWith(`${parentPath}/`) || path.length <= parentPath.length + 1;
	const routeError = duplicate
		? 'A view already uses this route pattern. Choose a different child path.'
		: invalidParent
			? 'The child route must extend the parent route.'
			: undefined;
	const canCreate = title.trim().length > 0 && !routeError;
	const create = () => {
		if (!canCreate) return;
		onCreate({
			Id: uuid(),
			Type: 'View',
			Title: title.trim(),
			RoutePath: path,
			RequiresAuthentication: parent.RequiresAuthentication,
			Children: [],
			Layout: {},
		});
	};

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>New child view</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<TextField autoFocus label="Title" value={title} onChange={(event) => setTitle(event.target.value)} required fullWidth />
					<TextField
						label="Route path"
						value={routePath}
						onChange={(event) => setRoutePath(event.target.value)}
						required
						fullWidth
						error={!!routeError}
						helperText={routeError ?? 'Colon-prefixed segments are placeholders supplied by links from parent pages.'}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button variant="contained" onClick={create} disabled={!canCreate}>
					Create
				</Button>
			</DialogActions>
		</Dialog>
	);
}
